// Pure chip-math utilities for the visual betting display.
// Everything here is deterministic (seeded) and React-free so it can be unit tested.
// IMPORTANT: all rounding/dividing below is VISUAL ONLY — the true bet/pot value is
// never altered; labels always render the real amount.

export const DENOMS = [50000, 10000, 5000, 2500, 1000, 500, 100, 50, 25, 5, 1];

export const CHIP_COLORS = {
  1:     { bg: '#f8fafc', edge: '#94a3b8' },   // white
  5:     { bg: '#dc2626', edge: '#fecaca' },   // red
  25:    { bg: '#4ade80', edge: '#dcfce7' },   // light green
  50:    { bg: '#1e3a8a', edge: '#bfdbfe' },   // dark blue
  100:   { bg: '#111827', edge: '#9ca3af' },   // black
  500:   { bg: '#7c3aed', edge: '#ddd6fe' },   // purple
  1000:  { bg: '#cbd5e1', edge: '#64748b' },   // light grey
  2500:  { bg: '#166534', edge: '#bbf7d0' },   // dark green
  5000:  { bg: '#7dd3fc', edge: '#0369a1' },   // light blue
  10000: { bg: '#4b5563', edge: '#d1d5db' },   // dark grey
  50000: { bg: '#4c1d95', edge: '#c4b5fd' },   // dark purple
};

const MAX_CHIPS = 10;
const MAX_DENOMS = 4;
export const MAX_REPRESENTABLE = 500000; // ten 50000 chips

// Tables with fractional blinds (e.g. 0.01/0.02) count cents as chip units, so a
// 0.04 bet renders as four white chips. Visual only — labels still show 0.04.
export const getChipScale = (smallBlind, bigBlind) => {
  const fractional = (v) => v > 0 && !Number.isInteger(Number(v));
  return fractional(smallBlind) || fractional(bigBlind) ? 100 : 1;
};

// Display money like ActionPanel does: whole numbers as-is, fractions to 2 decimals.
export const formatAmount = (n) => {
  const num = Number(n);
  if (isNaN(num)) return '0';
  return Number.isInteger(num) ? num.toLocaleString() : num.toFixed(2);
};

// Greedy largest-first breakdown of a bet into chip groups [{denom, count}, ...],
// capped at 10 chips total and 4 distinct denominations — once a cap would be
// violated we round down / stop. Skipped denominations (count 0) don't count as used.
// Values too big for ten 50000 chips are divided by 10 until representable.
export const breakdownBet = (trueAmount, scale = 1) => {
  let v = Math.round(Number(trueAmount) * scale);
  if (!isFinite(v) || v <= 0) return [];
  while (v > MAX_REPRESENTABLE) v = Math.floor(v / 10);

  const groups = [];
  let total = 0;
  for (const d of DENOMS) {
    let n = Math.floor(v / d);
    if (n === 0) continue;                     // not used — doesn't count as a denomination
    if (groups.length === MAX_DENOMS) break;   // would be a 5th denomination — stop
    n = Math.min(n, MAX_CHIPS - total);        // 10-chip cap
    if (n === 0) break;
    groups.push({ denom: d, count: n });
    total += n;
    v -= n * d;
    if (total === MAX_CHIPS) break;
  }
  return groups;
};

export const flattenGroups = (groups) => groups.flatMap((g) => Array(g.count).fill(g.denom));

// Split chips into columns of at most maxPerColumn (9 chips -> [5, 4]).
// Each column array is ordered bottom -> top, largest denominations at the bottom.
export const toColumns = (groups, maxPerColumn = 5) => {
  const flat = flattenGroups(groups);
  const columns = [];
  for (let i = 0; i < flat.length; i += maxPerColumn) {
    columns.push(flat.slice(i, i + maxPerColumn));
  }
  return columns;
};

// FNV-1a string hash — stable seeds like `${gameId}:${seat}:${amount}`.
export const hashStr = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// Small seeded PRNG — random at runtime (seed varies per hand/seat/amount) but
// stable across React re-renders so all-in pyramids never reshuffle mid-hand.
export const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// All-in stack: normal breakdown plus 10 extra chips (5 of the biggest denomination
// used, 5 of the smallest), shuffled into a random order and arranged as a 5-column
// pyramid with a strictly-tallest center. Leftover chips beyond the perfect pyramid
// go to random non-center columns. Columns may exceed 5 chips here (all-in only).
export const buildAllInPyramid = (trueAmount, seed, scale = 1) => {
  const groups = breakdownBet(trueAmount, scale);
  if (groups.length === 0) return [];
  const rng = mulberry32(seed);

  const biggest = groups[0].denom;
  const smallest = groups[groups.length - 1].denom;
  const flat = [
    ...flattenGroups(groups),
    ...Array(5).fill(biggest),
    ...Array(5).fill(smallest),
  ];

  // Fisher-Yates — randomizes the order denominations get stacked in the pyramid
  for (let i = flat.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [flat[i], flat[j]] = [flat[j], flat[i]];
  }

  const N = flat.length; // 11..20 (base <=10 plus the 10 extras)
  // Perfect step-1 pyramid heights [k-2, k-1, k, k-1, k-2]; pick largest k that fits
  let k = 3;
  while (5 * (k + 1) - 6 <= N) k++;
  const heights = [k - 2, k - 1, k, k - 1, k - 2];
  let leftover = N - (5 * k - 6); // always 0..4

  while (leftover > 0) {
    // Never add to the center column, and keep it strictly tallest
    const cands = [0, 1, 3, 4].filter((i) => heights[i] <= heights[2] - 2);
    if (cands.length > 0) {
      heights[cands[Math.floor(rng() * cands.length)]] += 1;
    } else {
      heights[2] += 1; // rare: re-open headroom so the sides can keep absorbing
    }
    leftover -= 1;
  }

  // Fill columns bottom-up from the shuffled chips
  const columns = [];
  let idx = 0;
  for (const h of heights) {
    columns.push(flat.slice(idx, idx + h));
    idx += h;
  }
  return columns;
};

// First few chips of a breakdown — used for the small "flying" clones in animations.
export const flightDenoms = (trueAmount, scale = 1) =>
  flattenGroups(breakdownBet(trueAmount, scale)).slice(0, 3);

// Smallest legal raise-TO amount (No-Limit): the current bet level plus the larger of
// one big blind or the size of the last bet/raise. `players` is the seat→player map.
//  - Fresh street, nobody has bet (level 0)        -> one big blind   (e.g. 2)
//  - Preflop, big blind is the standing bet (level=BB) -> 2× big blind (e.g. 4)
//  - Facing a bet/raise of size R                  -> level + max(BB, R)
// The last bet/raise size R is inferred as the gap between the current level and the
// next-highest committed bet, which is exact except in rare raise-then-call lines,
// where it errs slightly high (never low, so it can never offer an illegal raise).
export const minRaiseTo = (players = {}, currentBetLevel = 0, bigBlind = 0) => {
  const level = Number(currentBetLevel) || 0;
  let highestBelow = 0;
  for (const p of Object.values(players || {})) {
    const bet = Number(p?.currentBet) || 0;
    if (bet < level && bet > highestBelow) highestBelow = bet;
  }
  const lastRaiseSize = level - highestBelow; // 0 when no bet has been made this street
  return level + Math.max(Number(bigBlind) || 0, lastRaiseSize);
};

// Infer who won the hand from chip deltas between two player snapshots (the backend
// has no winner event yet). next.currentBet is added back so blinds posted in the
// same GAME_DEAL payload don't mask the winner's gain. Positive entries are pot
// winners — split pots simply produce several positive seats.
export const computeWinnings = (prevPlayers = {}, nextPlayers = {}) => {
  const winnings = {};
  for (const [seat, p] of Object.entries(nextPlayers)) {
    const prev = prevPlayers[seat];
    if (!prev || prev.isEmpty || !p || p.isEmpty) continue;
    const delta = ((p.chips ?? 0) + (p.currentBet ?? 0)) - (prev.chips ?? 0);
    if (delta > 0) winnings[seat] = delta;
  }
  return winnings;
};
